import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Share2, Globe, Lock, Copy, Check, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../../config';

export default function ShareDocumentModal({ isOpen, onClose, document, space, publishSettings, onPublishChange }) {
  const [isPublic, setIsPublic] = useState(publishSettings?.is_published || false);
  const [copied, setCopied] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Public docs preview now uses the space id in the URL.
  const publicSpaceId = space?.id;
  // Share link must be the SPA origin (where /docs/... is served), not the API host.
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const shareUrl = `${baseUrl}/docs/${publicSpaceId}/${document?.id}`;

  const handleTogglePublic = async (checked) => {
    setUpdating(true);
    try {
      if (onPublishChange) {
        await onPublishChange(checked);
      }
      setIsPublic(checked);
      toast.success(checked ? 'Document is now public' : 'Document is now private');
    } catch (error) {
      toast.error('Failed to update visibility');
    } finally {
      setUpdating(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="share-document-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <Share2 className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </div>
            <span>Share document</span>
          </DialogTitle>
          <DialogDescription>
            Control who can view this document and share the link.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {/* Visibility Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
            <div className="flex items-center gap-3">
              {isPublic ? (
                <Globe className="w-5 h-5 text-green-600" />
              ) : (
                <Lock className="w-5 h-5 text-slate-400" />
              )}
              <div>
                <p className="font-medium text-slate-900 dark:text-white">
                  {isPublic ? 'Public' : 'Private'}
                </p>
                <p className="text-sm text-slate-500">
                  {isPublic ? 'Anyone with the link can view' : 'Only team members can view'}
                </p>
              </div>
            </div>
            <Switch
              checked={isPublic}
              onCheckedChange={handleTogglePublic}
              disabled={updating}
              data-testid="visibility-toggle"
            />
          </div>

          {/* Share Link */}
          {isPublic && (
            <div className="space-y-2">
              <Label>Share link</Label>
              <div className="flex gap-2">
                <Input
                  value={shareUrl}
                  readOnly
                  className="font-mono text-sm"
                  data-testid="share-url-input"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                  data-testid="copy-share-url-button"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => window.open(shareUrl, '_blank')}
                  data-testid="open-share-url-button"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Preview Card */}
          {isPublic && (
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <div className="p-4 bg-white dark:bg-slate-900">
                <p className="font-medium text-slate-900 dark:text-white mb-1">
                  {document?.title || 'Untitled Document'}
                </p>
                <p className="text-sm text-slate-500 truncate">
                  {shareUrl}
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
